<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useStore } from '~/store'
import Alert from '~/ui/Alert.vue'
import Button from '~/ui/Button.vue'

interface MessageAction {
  text: string
  variant?: 'primary' | 'destructive'
  click?: () => void
}

interface Message {
  content: string
  key: string
  color?: 'blue' | 'red' | 'green'
  error?: boolean | string
  date?: Date
  displayTime?: number
  actions?: MessageAction[]
}

const props = defineProps<{ message: Message }>()
const isVisible = ref(true)
const store = useStore()

const messageColor = computed(() => {
  if (props.message.color) {
    return props.message.color
  }

  if (props.message.error || props.message.content?.toLowerCase().includes('error')) {
    return 'red'
  }

  return 'blue'
})

const dismiss = () => {
  isVisible.value = false
  store.commit('ui/removeMessage', props.message.key)
}

const runAction = (action: MessageAction) => {
  action.click?.()
  dismiss()
}

onMounted(() => {
  // displayTime: 0 means the message stays until an action dismisses it
  if (!props.message.displayTime) return

  setTimeout(dismiss, props.message.displayTime)
})
</script>

<template>
  <Transition name="fade">
    <Alert
      v-if="isVisible"
      role="alert"
      :class="`color-${messageColor}-solid`"
    >
      <div
        v-if="message.displayTime"
        class="progress"
        :style="{ animationDuration: `${message.displayTime}ms` }"
      />
      {{ message.content }}
      <template v-if="message.actions?.length" #actions>
        <Button
          v-for="action in message.actions"
          :key="action.text"
          :class="action.variant && `color-${action.variant}-solid`"
          ui-block-size="24"
          @click="runAction(action)"
        >
          {{ action.text }}
        </Button>
      </template>
    </Alert>
  </Transition>
</template>

<style scoped>
.progress {
  height: 3px;
  margin-bottom: 8px;
  border-radius: 2px;
  background: currentColor;
  opacity: .4;
  transform: scaleX(0);
  transform-origin: left;
  animation-name: message-progress;
  animation-timing-function: linear;
  animation-fill-mode: forwards;
}

@keyframes message-progress {
  to {
    transform: scaleX(1);
  }
}
</style>
